//Proofed
import { useState } from 'react'
import { Box, Container, Grid, Typography, Stack, Chip } from '@mui/material'
import { Link } from 'react-router-dom'
import { BookDemoModal } from '../../components/BookDemoModal'
import { FancyGoldButton } from '../../components/FancyGoldButton'
import { FancyLightButton } from '../../components/FancyLightButton'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'
import { FaqSection } from '../../components/FaqSection'
import StarIcon from '@mui/icons-material/Star'
import { COLOR_TERTIARY, EXPAND_HERO_START, EXPAND_HERO_MID, CHECKCIRCLE_SIZE, SECTION_DARK, DARK_800, BG_LIGHT, NAVBAR_BG } from '../../theme'
import { HowItWorksSection } from '../../components/HowItWorksSection'
import { TrustedPartnerSection } from '../../components/TrustedPartnerSection'
import { WaveDivider } from '../../components/WaveDivider'
import { ChallengesSection } from '../../components/ChallengesSection'
import { SolutionStack } from '../../components/SolutionStack'
import { CtaSection } from '../../components/CtaSection'
import PageMeta from '../../components/PageMeta'
import { RelatedSolutionsSection } from '../../components/RelatedSolutionsSection'
import { OutcomesSection } from '../../components/OutcomesSection'
import { SelfQualificationSection } from '../../components/SelfQualificationSection'
import {
    expandSectorsHighlights,
    expandSectorsChallenges,
    expandSectorsSolutionStack,
    expandSectorsSteps,
    expandSectorsOutcomes,
    expandSectorsQualifiers,
    expandSectorsFaqs,
    expandSectorsRelated,
} from '../../common/SolutionExpandSectorsContent'

const C = COLOR_TERTIARY

export default function ExpandNewSectors() {
    const [demoOpen, setDemoOpen] = useState(false)

    return (
        <Box>
            <PageMeta
                title="Expand into New Sectors"
                description="Scale your public sector BD operation into new sectors. Assign dedicated monitors, give each lead focused intelligence and increase bid throughput without adding headcount."
                canonical="/solutions/expand-into-new-sectors"
            />
            {/* ── 1. Hero  light left / dark panel right ─────────────────────── */}
            <Box sx={{
                background: `linear-gradient(110deg, white 0%, ${C}25 55%, ${C}45 100%)`,
                py: { xs: 6, md: 10 },
            }}>
                <Container maxWidth="lg">
                    <Grid container spacing={5} alignItems="stretch">
                        {/* Left  copy */}
                        <Grid item xs={12} md={6}>
                            <Chip label="Expanding Reach · Multi-Sector Growth" size="small" sx={{
                                mb: 3, backgroundColor: `${C}12`, color: C,
                                border: `1px solid ${C}40`, fontWeight: 700,
                                fontSize: 18, p: 1,
                                "& .MuiChip-label": {
                                    p: 1.5,
                                }
                            }} />
                            <Typography variant="h1" sx={{ fontSize: { xs: '36px', md: '52px' }, mb: 2, color: NAVBAR_BG, lineHeight: 1.15 }}>
                                Scale Your Public Sector Reach{' '}
                                <Box component="span" sx={{ color: C }}>Across Multiple Sectors</Box>
                            </Typography>
                            <Typography variant="h5" sx={{ color: 'text.secondary', fontWeight: 400, mb: 4, lineHeight: 1.6 }}>
                                Growth means more opportunities, but also more noise, more bids and more pressure on your team. Here's how to manage all of it
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <FancyGoldButton to="/pricing" color={C} endIcon={<ArrowForwardIcon />}>
                                    Get Started
                                </FancyGoldButton>
                                <FancyLightButton onClick={() => setDemoOpen(true)} color={C}>
                                    Book a Discovery Call
                                </FancyLightButton>
                            </Stack>
                        </Grid>

                        {/* Right  dark highlights panel */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                background: `linear-gradient(135deg, ${EXPAND_HERO_START} 0%, ${EXPAND_HERO_MID} 100%)`,
                                border: `1px solid ${C}30`,
                                borderRadius: 3,
                                p: { xs: 3, md: 4 },
                            }}>
                                <Typography variant="caption" sx={{
                                    color: C, textTransform: 'uppercase',
                                    letterSpacing: '1px', fontWeight: 700,
                                }}>
                                    With this solution you'll be able to
                                </Typography>
                                <Stack spacing={1.75} sx={{ mt: 2.5 }}>
                                    {expandSectorsHighlights.map(h => (
                                        <Box key={h} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                            <CheckCircleIcon sx={{ color: C, fontSize: CHECKCIRCLE_SIZE, mt: 0.15, flexShrink: 0 }} />
                                            <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', lineHeight: 1.6 }}>{h}</Typography>
                                        </Box>
                                    ))}
                                </Stack>

                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <WaveDivider top={`linear-gradient(110deg, white 0%, ${C}25 55%, ${C}45 100%)`} bottom={DARK_800} />
            {/* ── 2. Challenges  dark ─────────────────────────────────────────── */}
            <ChallengesSection
                title="Three pressures that come with rapid growth"
                subtitle="Growing into multiple sectors creates new operational demands. These are the ones that most commonly cause fast growing businesses to plateau"
                items={expandSectorsChallenges}
                color={C}
            />

            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />
            {/* ── 3. Solution stack  light ────────────────────────────────────── */}
            <SolutionStack
                subtitle="Three components designed for businesses scaling across sectors, dedicated monitoring per lead, AI-assisted bid writing at volume and a strategic review to ensure your resource is invested where it delivers the best return"
                items={expandSectorsSolutionStack}
                color={C}
            />
            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            {/* ── 4. Journey  dark (shared HowItWorksSection) ─────────────────── */}
            <HowItWorksSection
                title="Your route to multi-sector scale"
                subtitle="Three steps from fragmented growth to a structured, high-performing multi-sector operation"
                steps={expandSectorsSteps}
                dark={true}
            />
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />

            {/* ── 5. Outcomes  light ──────────────────────────────────────────── */}
            <OutcomesSection
                subtitle="After assigning dedicated licences, deploying Tender Ninja and completing a Win Consultancy review, this is the position you'll be in"
                items={expandSectorsOutcomes}
                color={C}
            />

            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            {/* ── 6. Self-qualification  dark ─────────────────────────────────── */}
            <SelfQualificationSection
                items={expandSectorsQualifiers}
                color={C}
                onDemoClick={() => setDemoOpen(true)}
            />
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />

            {/* ── 9. Related solutions  light ─────────────────────────────────── */}
            <RelatedSolutionsSection
                subtitle="The platform grows with you. Here's where most multi-sector businesses head next."
                items={expandSectorsRelated}
                color={C}
            />

            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            {/* ── 8. FAQ  dark ────────────────────────────────────────────────── */}
            <FaqSection
                title='Common questions'
                subtitle="From businesses scaling into new sectors who've asked these questions before you"
                items={expandSectorsFaqs}
                color={DARK_800}
            />
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />

            <TrustedPartnerSection />

            <CtaSection
                title="Ready to scale your public sector reach?"
                subtitle="Book a no-obligation discovery call. We'll confirm the right licence structure and solution for your team"
                onDemoClick={() => setDemoOpen(true)}
            />

            <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
        </Box>
    )
}
